import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { useProject } from '../../context/ProjectContext';
import visitService from '../../services/visitService';
import { VISIT_TYPES, formatVisitId } from '../../utils/constants';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import Pagination from '../Common/Pagination';
import { Modal } from '../Common/Modal';
import VisitLetterFilters from '../VisitLetters/VisitLetterFilters';
import UnlockTransactionGrid, { SORT_KEYS } from './UnlockTransactionGrid';
import '../Common/DataGrid.css';
import '../MailManagement/MailManagement.css';
import '../VisitLetters/VisitLetterDownloads.css';
import './UnlockTransactionList.css';

const getVisitId = (row) =>
  row?.scheduledVisitID ?? row?.ScheduledVisitID ?? row?.scheduledVisitId;

const UnlockTransactionList = () => {
  const { currentProject: contextProject } = useProject();
  const projectCode = contextProject && contextProject !== 'all'
    ? (typeof contextProject === 'string' ? contextProject : contextProject?.projectCode ?? contextProject?.ProjectCode)
    : null;

  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [visitType, setVisitType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [sortBy, setSortBy] = useState('followUpConfirmedOn');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [remark, setRemark] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const fetchSites = useCallback(async () => {
    if (!projectCode) {
      setSites([]);
      return;
    }
    try {
      const response = await api.get(`/sites?project=${encodeURIComponent(projectCode)}`);
      const sitesList = (response.data || [])
        .map((s) => {
          const siteCode = (s.siteCode ?? s.SiteCode ?? s.vSiteCode ?? '').toString().trim();
          const siteDescription = (s.siteDescription ?? s.SiteDescription ?? s.vSiteDescription ?? s.sitedescription ?? '').toString().trim();
          const siteDisplay = [siteCode, siteDescription].filter(Boolean).join(' - ') || siteCode || siteDescription;
          return {
            siteID: s.siteID ?? s.siteId ?? s.SiteNo ?? s.nSiteNo,
            siteCode,
            siteDescription,
            siteDisplay,
          };
        })
        .filter((s) => s.siteCode)
        .sort((a, b) => a.siteCode.localeCompare(b.siteCode));
      setSites(sitesList);
    } catch (err) {
      console.warn('Failed to load sites for unlock transactions:', err);
      setSites([]);
    }
  }, [projectCode]);

  const fetchRows = useCallback(async () => {
    if (!projectCode) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await visitService.getUnlockEligibleVisits(projectCode);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to load visits.';
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectCode]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    setPage(1);
  }, [siteId, visitType, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const selectedSiteCode = useMemo(() => {
    if (!siteId) return null;
    const site = sites.find((s) => String(s.siteID ?? s.siteId) === String(siteId));
    return site?.siteCode?.trim() || null;
  }, [siteId, sites]);

  const filteredAndSorted = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    let result = rows;

    if (selectedSiteCode) {
      result = result.filter((row) => {
        const code = (row.siteCode ?? row.SiteCode ?? '').toString().trim();
        return code === selectedSiteCode;
      });
    }

    if (visitType) {
      result = result.filter((row) => (row.visitType ?? row.VisitType) === visitType);
    }

    if (searchLower) {
      result = result.filter((row) => {
        const visitId = formatVisitId(row);
        const site = (row.siteCode ?? row.SiteCode ?? '').toString();
        const vt = (row.visitType ?? row.VisitType ?? '').toString();
        const vtLabel = (VISIT_TYPES[vt] || vt).toString();
        const cra = (row.craName ?? row.CRAName ?? '').toString();
        const stage = (row.crfStage ?? row.CrfStage ?? '').toString();
        const text = [visitId, site, vt, vtLabel, cra, stage].join(' ').toLowerCase();
        return text.includes(searchLower);
      });
    }

    const key = SORT_KEYS[sortBy] || SORT_KEYS.followUpConfirmedOn;
    result = [...result].sort((a, b) => {
      let va = a[key] ?? a[key.charAt(0).toUpperCase() + key.slice(1)];
      let vb = b[key] ?? b[key.charAt(0).toUpperCase() + key.slice(1)];
      if (key === 'followUpConfirmedOn') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return sortOrder === 'asc' ? va - vb : vb - va;
      }
      if (key === 'scheduledVisitID') {
        va = Number(va) || 0;
        vb = Number(vb) || 0;
        return sortOrder === 'asc' ? va - vb : vb - va;
      }
      if (key === 'displayVisitCode') {
        va = formatVisitId(a).toLowerCase();
        vb = formatVisitId(b).toLowerCase();
        if (va < vb) return sortOrder === 'asc' ? -1 : 1;
        if (va > vb) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
      va = (va ?? '').toString().toLowerCase();
      vb = (vb ?? '').toString().toLowerCase();
      if (va < vb) return sortOrder === 'asc' ? -1 : 1;
      if (va > vb) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [rows, selectedSiteCode, visitType, searchTerm, sortBy, sortOrder]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAndSorted.slice(start, start + pageSize);
  }, [filteredAndSorted, page, pageSize]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const openUnlockModal = (row) => {
    setUnlockTarget(row);
    setRemark('');
  };

  const closeUnlockModal = () => {
    if (unlocking) return;
    setUnlockTarget(null);
    setRemark('');
  };

  const handleUnlock = async () => {
    const id = getVisitId(unlockTarget);
    const remarkTrim = (remark || '').trim();
    if (id == null) return;
    if (!remarkTrim) {
      setToast({ type: 'error', message: 'Unlock reason is required.' });
      return;
    }
    setUnlocking(true);
    try {
      await visitService.unlockVisit(id, remarkTrim);
      setToast({ type: 'success', message: 'Visit unlocked. It is now available on the In Progress tab.' });
      closeUnlockModal();
      await fetchRows();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to unlock visit.';
      setToast({ type: 'error', message: msg });
    } finally {
      setUnlocking(false);
    }
  };

  const emptyProjectMessage = !projectCode
    ? 'Please select a project to view unlock-eligible visits.'
    : null;

  const emptyFilterMessage = projectCode && !loading && filteredAndSorted.length === 0 && rows.length > 0
    ? 'No visits match the selected filters.'
    : 'No fully completed visits eligible for unlock.';

  return (
    <div className="unlock-transaction-page visit-letter-downloads-page mail-management-page">
      {toast && (
        <Alert type={toast.type === 'error' ? 'error' : 'success'} message={toast.message} onClose={() => setToast(null)} />
      )}
      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {emptyProjectMessage && <Alert type="info" message={emptyProjectMessage} autoClose={false} />}

      <div className="card">
        <VisitLetterFilters
          sites={sites}
          siteId={siteId}
          visitType={visitType}
          searchTerm={searchTerm}
          onSiteChange={setSiteId}
          onVisitTypeChange={setVisitType}
          onSearchChange={setSearchTerm}
          disabled={!projectCode || loading}
        />

        <div className="visit-letter-grid-wrap">
          {loading && rows.length === 0 ? (
            <Spinner />
          ) : (
            <>
              <div className="data-grid-toolbar">
                <div className="data-grid-controls">
                  <div className="data-grid-filter">
                    <span className="data-grid-filter-label">Page size</span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="form-select data-grid-filter-select data-grid-page-size"
                    >
                      <option value={5}>5 items</option>
                      <option value={10}>10 items</option>
                      <option value={25}>25 items</option>
                      <option value={50}>50 items</option>
                    </select>
                  </div>
                </div>
              </div>

              <UnlockTransactionGrid
                rows={pageRows}
                loading={loading}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                onUnlock={openUnlockModal}
                emptyMessage={emptyFilterMessage}
              />

              {!loading && filteredAndSorted.length > 0 && (
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalItems={filteredAndSorted.length}
                  onPageChange={setPage}
                  onPageSizeChange={handlePageSizeChange}
                  showPageSize={false}
                />
              )}
            </>
          )}
        </div>
      </div>

      {unlockTarget && createPortal(
        <Modal
          isOpen={!!unlockTarget}
          onClose={closeUnlockModal}
          title={`Unlock visit ${formatVisitId(unlockTarget)}`}
          size="medium"
          className="unlock-transaction-modal"
          closeOnOverlayClick={!unlocking}
          footer={(
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeUnlockModal}
                disabled={unlocking}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUnlock}
                disabled={unlocking}
              >
                {unlocking ? 'Unlocking…' : 'Confirm Unlock'}
              </button>
            </>
          )}
        >
          <p className="unlock-transaction-modal-message">
            This will move the visit back to <strong>In Progress</strong> so CRA can edit the report and complete the workflow again.
          </p>
          <div className="form-group">
            <label className="form-label" htmlFor="unlock-remark">
              Reason <span className="required-asterisk">*</span>
            </label>
            <textarea
              id="unlock-remark"
              className="form-textarea"
              rows={4}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              disabled={unlocking}
              placeholder="Enter reason for unlocking this transaction"
              maxLength={2000}
            />
          </div>
        </Modal>,
        document.body
      )}
    </div>
  );
};

export default UnlockTransactionList;
